import { useEffect, useMemo, useState } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";

import type { Book } from "@/schemas";
import { formatCurrency } from "@/lib/formatters";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getPerformanceResult } from "../services/portfolio-service";
import { getCurrentValuation } from "../services/portfolio-service";

interface AllPortfoliosBreakdownProps {
  books: Book[];
  currencyCode: string;
  onSelectPortfolio?: (bookId: string) => void;
  selectedBookId?: string;
}

interface PortfolioBreakdownItem {
  bookId: string;
  name: string;
  value: number;
  dayPerformanceAbsolute: number | null;
  dayPerformancePercentage: number | null;
}

export function AllPortfoliosBreakdown({
  books,
  currencyCode,
  onSelectPortfolio,
  selectedBookId,
}: AllPortfoliosBreakdownProps) {
  const [items, setItems] = useState<PortfolioBreakdownItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchBreakdown = async () => {
      if (books.length === 0) {
        if (isMounted) {
          setItems([]);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);

        const nextItems = await Promise.all(
          books.map(async (book) => {
            const [snapshot, dailyPerformance] = await Promise.all([
              getCurrentValuation(book.book_id),
              getPerformanceResult(book.book_id, "1d"),
            ]);
            const value = snapshot?.total_current_value ?? 0;
            const dayPerformanceAbsolute =
              dailyPerformance?.data?.total_performance?.total_performance_absolute ?? null;
            const dayPerformancePercentage =
              dailyPerformance?.data?.total_performance?.total_performance_percentage ?? null;

            return {
              bookId: book.book_id,
              name: book.name,
              value,
              dayPerformanceAbsolute,
              dayPerformancePercentage,
            };
          }),
        );

        const sorted = nextItems.sort((a, b) => b.value - a.value);

        if (isMounted) {
          setItems(sorted);
        }
      } catch (error) {
        console.error("Failed to fetch all-portfolios breakdown:", error);
        if (isMounted) {
          setItems([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchBreakdown();

    return () => {
      isMounted = false;
    };
  }, [books]);

  const totalValue = useMemo(
    () => items.reduce((accumulator, item) => accumulator + item.value, 0),
    [items],
  );

  return (
    <Card className="shadow-sm border-border/40">
      <CardHeader>
        <CardTitle>All Portfolios Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-[104px] w-full rounded-xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No portfolio value data available yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {items.map((item) => {
              const share = totalValue > 0 ? (item.value / totalValue) * 100 : 0;
              const dayAbsolute = item.dayPerformanceAbsolute;
              const dayPerformance = item.dayPerformancePercentage;
              const isMissingPerformance = dayPerformance === null || dayAbsolute === null;
              const isNegative =
                !isMissingPerformance && (dayPerformance < -1e-9 || dayAbsolute < -1e-9);
              const isPositive = !isMissingPerformance && !isNegative;
              const absoluteDayValue = isMissingPerformance ? 0 : Math.abs(dayAbsolute);
              const absoluteDayPercent = isMissingPerformance ? 0 : Math.abs(dayPerformance);
              const performanceBadgeClassName = isMissingPerformance
                ? "bg-muted text-muted-foreground"
                : isPositive
                  ? "bg-emerald-500/10 text-emerald-600"
                  : "bg-red-500/10 text-red-600";

              return (
                <button
                  key={item.bookId}
                  type="button"
                  onClick={() => onSelectPortfolio?.(item.bookId)}
                  className={`w-full text-left rounded-xl border px-3 sm:px-4 py-2.5 sm:py-3 transition-colors ${
                    selectedBookId === item.bookId
                      ? "border-primary/60 bg-primary/5"
                      : "border-border/50 bg-muted/10 hover:bg-muted/20"
                  }`}
                >
                  <p className="text-xs sm:text-sm font-medium truncate">{item.name}</p>
                  <p className="mt-1.5 sm:mt-2 text-base sm:text-xl font-semibold tabular-nums">
                    {formatCurrency(item.value, currencyCode)}
                  </p>
                  <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground">
                    {share.toFixed(2)}% of total
                  </p>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">1D change</p>
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] sm:text-xs font-medium ${performanceBadgeClassName}`}
                    >
                      {isMissingPerformance ? (
                        <span>N/A</span>
                      ) : (
                        <>
                          {isPositive ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          <span>
                            {isPositive ? "+" : "-"}
                            {formatCurrency(absoluteDayValue, currencyCode, 2)}
                          </span>
                          <span className="opacity-70">
                            ({isPositive ? "+" : "-"}
                            {absoluteDayPercent.toFixed(2)}%)
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
